import { supplierQuantityToMicrounits } from "./supplier-unit-price";

export type PoBrand = { id: string; itemId: string; name: string; nameAr: string; active: boolean; usualSupplierIds: string[] };
export type BrandSplit = { brandId: string; orderedQty: string | number };

export function usualPoBrand(brands: PoBrand[], itemId: string, supplierId: string): string | null {
  const matches = brands.filter(b => b.active && b.itemId === itemId && b.usualSupplierIds.includes(supplierId));
  return matches.length === 1 ? matches[0].id : null;
}

export function splitRemainder(quantity: unknown, splits: BrandSplit[]): bigint {
  const original = supplierQuantityToMicrounits(quantity);
  if (original <= 0n) throw new Error("Original quantity must be positive.");
  return original - splits.reduce((sum, row) => sum + supplierQuantityToMicrounits(row.orderedQty), 0n);
}

export function formatBrandQuantity(value: bigint): string {
  const sign = value < 0n ? "-" : "";
  const absolute = value < 0n ? -value : value;
  return sign + `${absolute / 1000000n}.${String(absolute % 1000000n).padStart(6, "0")}`.replace(/\.?0+$/, "");
}

/** A split remains nested in the request; the server, not the browser, expands it. */
export function expandPoBrandSplits(lines: any[], previous: any[] = []): any[] {
  if (!Array.isArray(lines)) throw new Error("Order lines must be an array.");
  return lines.flatMap(line => {
    if (line.brandSplits === undefined) return [line];
    if (!Array.isArray(line.brandSplits) || line.brandSplits.length === 0) throw new Error("A brand split requires rows.");
    const old = line.id ? previous.find(row => row.id === line.id) : null;
    if (line.id && !old) throw new Error("Split source line was not found.");
    if (old && (old.itemId !== line.itemId || old.unit !== line.unit ||
      supplierQuantityToMicrounits(old.orderedQty) !== supplierQuantityToMicrounits(line.orderedQty))) {
      throw new Error("Split quantity, item and unit must match the original saved line.");
    }
    if (splitRemainder(line.orderedQty, line.brandSplits) !== 0n) throw new Error("Brand quantities must add up exactly to the original quantity.");
    const seen = new Set<string>();
    for (const row of line.brandSplits) {
      if (!row.brandId || typeof row.brandId !== "string" || seen.has(row.brandId)) throw new Error("Each split row requires a different brand.");
      seen.add(row.brandId);
    }
    const { brandSplits, ...source } = line;
    // Zero allocations do not become invalid zero-quantity PO lines.
    return brandSplits.filter((row: BrandSplit) => supplierQuantityToMicrounits(row.orderedQty) > 0n)
      .map((row: BrandSplit, index: number) => ({
        ...source, id: index === 0 ? source.id : undefined, newBrandSplit: index > 0 || !source.id,
        brandId: row.brandId, orderedQty: row.orderedQty,
      }));
  });
}

export function poBrandLabel(line: { brandName?: string | null; brandNameAr?: string | null; hasBrands?: boolean }): string {
  return [line.brandName, line.brandNameAr].filter(Boolean).join(" / ") || (line.hasBrands ? "Any brand / أي علامة تجارية" : "");
}